/**
 * Statut de la demande dans le workflow, décalque de `PDS.PdsStatus` côté back-end.
 *
 * Seule autorité sur « qualifiée » : le back ne promeut `SELECTED` en `QUALIFIED` qu'une
 * fois la qualification complète, et l'import laisse toujours la demande en `SELECTED`.
 */
export type RequestStatus =
  | 'TO_SELECT'
  | 'SELECTED'
  | 'QUALIFIED'
  | 'TRANSFERED_TO_TOUR'
  | 'ASSIGNED_TO_SUBCONTRACTOR'
  | 'APPOINTMENT_IDENTIFIED'
  | 'APPOINTMENT_SCHEDULED'
  | 'WITHOUT_APPOINTMENT'
  | 'ASSIGNED_TO_TECHNICIAN'
  | 'DONE';

/**
 * Une demande d'intervention telle que la renvoie `GET pds/qualify/`.
 *
 * Champs relevés sur les colonnes et les accès `row.*` de la v1. Tout est optionnel
 * côté API sauf l'identifiant : le sérialiseur renvoie `null` pour un champ absent.
 */
export type RequestRow = {
  id: number;
  intervention_status: RequestStatus | null;
  service_point_customer_id: string | null;
  service_point_is_creation: boolean | null;
  address_street_number: string | null;
  address_street_name: string | null;
  city_data: { zip_code: string | null; name: string | null } | null;
  contract_data: { oss_code: string | null } | null;
  contact_name: string | null;
  contact_telephone: string | null;
  equipment_id: string | null;
  equipment_diameter: string | number | null;
  equipment_brand: string | null;
  equipment_model: string | null;
  equipment_family_type: string | null;
  equipment_parent_id: string | null;
  device_id: string | null;
  device_model: string | null;
  device_radio_mode: string | null;
  device_available_kms_keys_list: string[] | null;
  device_missing_kms_keys_list: string[] | null;
  /** Code du type d'intervention. L'API le renvoie en chaîne, pas en nombre. */
  intervention_type_code: string | number | null;
  intervention_cause: string | null;
  intervention_instructions: string | null;
  configuration_data: { name: string | null } | null;
  device_has_hourly_index: boolean | null;
  /** Bornes du rendez-vous posées à la qualification, ISO UTC. Début seul = au plus tôt, fin seule = au plus tard. */
  start_scheduled_time: string | null;
  end_scheduled_time: string | null;
};

/** Colonnes triables de la maquette. */
export type RequestSortColumn = 'servicePoint' | 'qualification';
export type SortDirection = 'asc' | 'desc';

/** Tiret affiché à la place d'une valeur absente, et repère d'absence côté rendu. */
export const PLACEHOLDER = '—';

/** Remplace une valeur absente ou vide par un tiret, plutôt que d'afficher « null ». */
export function displayValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return PLACEHOLDER;
  }

  const text = String(value).trim();

  return text === '' ? PLACEHOLDER : text;
}

/** « 6 Imp Des Tailleurs · 53810 Changé », en omettant les morceaux absents. */
export function formatAddress(row: RequestRow): string {
  const street = [row.address_street_number, row.address_street_name]
    .filter((part) => part !== null && String(part).trim() !== '')
    .join(' ');
  const city = [row.city_data?.zip_code, row.city_data?.name]
    .filter((part) => part !== null && part !== undefined && String(part).trim() !== '')
    .join(' ');

  return [street, city].filter((part) => part !== '').join(' · ');
}

/** « Sensus - iPERL », sans laisser de tiret orphelin si une moitié manque. */
export function formatSensorModel(row: RequestRow): string {
  const parts = [row.equipment_brand, row.equipment_model].filter(
    (part) => part !== null && String(part).trim() !== ''
  );

  return parts.length === 0 ? PLACEHOLDER : parts.join(' - ');
}

/**
 * Avancement de la qualification d'une demande. La valeur dit l'état des données ;
 * `partial` est présenté à l'utilisateur sous le mot « Incomplète », et `prefilled`
 * sous « Pré-remplie ».
 */
export type QualificationState = 'toQualify' | 'prefilled' | 'partial' | 'complete';

/**
 * Avancement de la qualification, lu d'abord sur `intervention_status`.
 *
 * Le back ne promeut `SELECTED` en `QUALIFIED` qu'une fois la qualification complète
 * (configuration, type, mode radio et index horaire) : c'est la seule marque fiable
 * qu'une qualification a bien eu lieu, et elle seule donne `complete`.
 *
 * Sous ce seuil, les deux champs disent où en est la saisie. Les deux posés sans
 * promotion ne peuvent venir que de l'import : lui seul applique la configuration par
 * défaut (APPSUPPORT-2701) et reporte le type du fichier sans passer par la
 * qualification. Une qualification manuelle, elle, renseigne du même geste
 * `device_radio_mode` et `device_has_hourly_index`, donc déclenche la promotion. D'où
 * `prefilled`, distinct d'une demande vierge comme d'une demande qualifiée.
 *
 * Un seul des deux posé trahit une saisie en cours — le formulaire n'exige pas le type,
 * et le back-end répond alors `missing_fields: ["intervention_type_code"]`.
 */
export function getQualificationState(row: RequestRow): QualificationState {
  if (row.intervention_status === 'QUALIFIED') {
    return 'complete';
  }

  // `!= null` et non `!== null` : l'API garantit le champ, mais une réponse partielle le
  // laisserait `undefined`, que la table ne doit pas prendre pour une valeur posée.
  const hasType = row.intervention_type_code != null;
  const hasConfiguration = row.configuration_data != null;

  if (hasType && hasConfiguration) {
    return 'prefilled';
  }

  return hasType || hasConfiguration ? 'partial' : 'toQualify';
}

/**
 * Une demande peut-elle partir en affectation sans repasser par le formulaire ?
 *
 * Les deux états où la qualification est renseignée : `complete`, que le back a promue en
 * `QUALIFIED`, et `prefilled`, que l'import a remplie sans la promouvoir. Restent dehors
 * la demande vierge et celle dont la saisie est en cours — il leur manque de quoi être
 * affectées.
 */
export function canSendToAssignment(row: RequestRow): boolean {
  const state = getQualificationState(row);

  return state === 'complete' || state === 'prefilled';
}

/**
 * Clé i18n de ce qui manque à une demande incomplète, `null` quand rien ne manque.
 *
 * Les deux seuls motifs identifiables depuis la ligne, puisque l'état ne se lit que sur
 * ces deux champs. Un refus du back-end pour un champ absent du payload de la ligne
 * reste hors de portée d'ici.
 */
export function missingQualificationKey(row: RequestRow): string | null {
  if (getQualificationState(row) !== 'partial') {
    return null;
  }

  return row.intervention_type_code === null
    ? 'interventionCreation.requests.missingInterventionType'
    : 'interventionCreation.requests.missingConfiguration';
}

/**
 * Clé i18n du critère d'index horaires, `null` quand le critère n'a pas été posé.
 *
 * La v1 voulait afficher les deux cas mais sa condition `valeur && valeur === false`
 * était inatteignable : « Désactivation » ne s'est jamais affichée. On reproduit
 * l'intention, pas le court-circuit.
 */
export function hourlyIndexLabelKey(row: RequestRow): string | null {
  if (row.device_has_hourly_index === null) {
    return null;
  }

  return row.device_has_hourly_index
    ? 'interventionCreation.requests.detail.withHourlyIndex'
    : 'interventionCreation.requests.detail.withoutHourlyIndex';
}

/** Vrai si la valeur est renseignée, une chaîne d'espaces comptant pour absente. */
function isFilled(value: string | number | null | undefined): boolean {
  return displayValue(value) !== PLACEHOLDER;
}

/** Le « père » du module n'est affiché que s'il existe : la v1 le laissait vide sinon. */
export function hasParent(row: RequestRow): boolean {
  return isFilled(row.equipment_parent_id);
}

/** Un contact peut n'avoir qu'un nom ou qu'un téléphone. */
export function hasContact(row: RequestRow): boolean {
  return isFilled(row.contact_name) || isFilled(row.contact_telephone);
}

/**
 * Libellé de la configuration cible. Trois cas distincts, comme en v1 : objet absent
 * (jamais qualifiée), `name` à `null` (configuration « Par défaut »), ou nom explicite.
 */
export function configurationLabel(row: RequestRow, t: (key: string) => string): string {
  if (row.configuration_data == null) {
    return PLACEHOLDER;
  }

  return row.configuration_data.name === null
    ? t('interventionCreation.requests.detail.defaultConfiguration')
    : displayValue(row.configuration_data.name);
}

/**
 * Tri côté client, commun aux deux étapes : le serveur renvoie tout d'un coup
 * (`size=9999`). La clé est fournie par l'appelant, c'est elle qui porte la règle
 * métier propre à chaque colonne.
 */
export function sortRows<T>(rows: T[], key: (row: T) => string, direction: SortDirection): T[] {
  const sorted = [...rows].sort((left, right) =>
    key(left).localeCompare(key(right), undefined, { numeric: true })
  );

  return direction === 'asc' ? sorted : sorted.reverse();
}

/**
 * Tri des demandes à qualifier. La colonne Qualification trie sur le seul code du type
 * d'intervention, pas sur l'état affiché : les demandes sans type se regroupent, puisque
 * la clé de tri leur vaut la chaîne vide.
 */
export function sortRequests(
  rows: RequestRow[],
  column: RequestSortColumn,
  direction: SortDirection
): RequestRow[] {
  return sortRows(
    rows,
    (row) =>
      column === 'servicePoint'
        ? (row.service_point_customer_id ?? '')
        : String(row.intervention_type_code ?? ''),
    direction
  );
}
